var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var CaseView_1;
import { LitElement, html, css } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { marked } from "marked";
import { SignalWatcher } from "@lit-labs/preact-signals";
import { scenario, draft, launch } from "../state";
import { api } from "../api/client";
import { startAutosave, stopAutosave } from "../state/autosave";
import "../shell/pas-banner";
import "../shell/chart-navigator";
import "../components/chart-viewer";
import "../components/save-indicator";
import "../modes/classify";
import "../modes/abstract";
import "../modes/audit";
import "../modes/sequence";
import "../modes/query";
let CaseView = class CaseView extends SignalWatcher(LitElement) {
    constructor() {
        super(...arguments);
        this.scenarioId = "";
        this.selectedDocId = null;
        this.result = null;
        this.submitting = false;
        this.submitError = null;
        this.assessment = null;
        // When the cap is hit, the submit response carries a title/ref for the support CTA.
        this.lockInfo = null;
        // Unlock-request flow on the locked screen (creates a Zoho Desk ticket server-side).
        this.supportState = "idle";
        this.supportTicket = null;
        // Set only when the scenario itself failed to load and there is nothing to render;
        // a mid-session draft-restore failure does NOT set this (see loadScenario).
        this.loadError = null;
        this.confirmFinalSubmit = false;
        this.attemptStart = 0;
    }
    static { CaseView_1 = this; }
    static { this.styles = css `
    /* box-sizing reset — the global one in base.css does NOT cross the shadow
       boundary, so without this padded grid items (panes) overflow their track. */
    :host, *, *::before, *::after { box-sizing: border-box; }
    /* container-type makes the 3-column layout respond to the EHR's OWN width
       (i.e. the embedding LMS iframe), not the laptop screen width — so it no
       longer collapses to a stacked view just because the browser window is
       narrow-ish. The host fills the iframe. A slight corner radius gives the
       whole EHR a soft "window" feel inside the lesson. */
    :host { display: grid; grid-template-rows: auto 1fr auto; height: 100vh;
            container-type: inline-size; container-name: ehr;
            border-radius: 10px; overflow: hidden;
            background: var(--color-slate-50, #f8fafc); }
    .body { display: grid; grid-template-columns: 184px minmax(0, 1fr) minmax(0, 420px);
            gap: 0; overflow: hidden; min-height: 0; }
    /* min-width:0 lets the panes shrink instead of overflowing — this is what
       stops the coding inputs (and any wide content) spilling past their box. */
    .pane { overflow: auto; min-height: 0; min-width: 0; }

    /* Only stack when the IFRAME itself is genuinely narrow (well below any
       15" laptop LMS lesson width). Calibrated against the real iframe width
       on staging. Above this, the full 3-pane view holds. */
    @container ehr (max-width: 759px) {
      /* Stacked: .body becomes the scroll container.
         TWO things that look right but aren't:
         1. It must be .body (a DESCENDANT), not :host — :host is itself the
            container (container-type), and an element cannot respond to its own
            container query, so :host rules in here are silently ignored.
         2. It must be display:BLOCK, not a 1-col grid — a fixed-height grid
            compresses its rows and the pane content (e.g. a long record)
            overflows its compressed row and spills over the next pane. Block
            stacking sizes each pane to its full content height. (Verified in
            docs/design/ehr-redesign-mockup.html.)
         The banner (row 1) and submit bar (row 3) stay pinned; .body scrolls. */
      .body { display: block; overflow-y: auto; overflow-x: hidden;
              background: var(--color-slate-200); }
      .pane { overflow: visible; border: 0; }
      .pane + .pane { margin-top: 8px; }
    }
    .pane.nav { background: var(--color-slate-100); border-right: 1px solid var(--color-slate-200); }
    .pane.chart { background: var(--color-white); padding: 18px 20px; }
    .pane.panel { background: var(--color-slate-50); border-left: 1px solid var(--color-slate-200);
                  padding: 16px; }
    .actions { display: flex; gap: 12px; align-items: center; padding: 12px 16px;
               background: var(--color-white); border-top: 1px solid var(--color-slate-200); }
    button.submit { display: inline-flex; align-items: center; gap: 8px;
                    padding: 10px 20px; border-radius: var(--radius-md, 6px);
                    background: var(--color-blue-600, #2563eb); color: white;
                    border: 0; cursor: pointer; font-weight: 600; }
    button.submit:hover { background: var(--color-blue-700, #1d4ed8); }
    button.submit:disabled { opacity: .7; cursor: progress; }
    .spinner { width: 15px; height: 15px; border-radius: 50%; flex: 0 0 auto;
               border: 2px solid rgba(255,255,255,.45); border-top-color: #fff;
               animation: spin .7s linear infinite; }
    @keyframes spin { to { transform: rotate(360deg); } }
    .attempt-counter { font-size: 13px; font-weight: 600; color: var(--color-slate-600); }
    .attempt-counter.final { color: var(--color-amber-600, #d97706); }
    .submit-error { font-size: 13px; color: var(--color-danger, #dc2626); }

    /* locked / contact-support screen */
    .locked { display: flex; flex-direction: column; gap: 14px; padding: 8px 2px; }
    .locked .lhead { display: flex; align-items: center; gap: 10px; }
    .locked .licon { width: 40px; height: 40px; border-radius: 50%; flex: 0 0 auto; display: grid;
                     place-items: center; background: #FFFBEB; color: #D97706; font-size: 20px; }
    .locked h2 { margin: 0; font-size: 17px; color: var(--color-slate-900); }
    .locked p { margin: 0; font-size: 13px; line-height: 1.55; color: var(--color-slate-600); }
    .locked .lcard { background: var(--color-slate-50); border: 1px solid var(--color-slate-200);
                     border-radius: var(--radius-md); padding: 12px 14px; font-size: 12px;
                     color: var(--color-slate-600); }
    button.support { align-self: flex-start; display: inline-flex; align-items: center; gap: 8px;
                     padding: 11px 20px; border-radius: var(--radius-md, 6px);
                     background: var(--color-blue-600, #2563eb); color: white; border: 0;
                     cursor: pointer; font-weight: 700; font-size: 14px; }
    button.support:hover { background: var(--color-blue-700, #1d4ed8); }
    button.support:disabled { opacity: .8; cursor: progress; }
    .support-err { color: var(--color-danger, #dc2626); }
    .support-done { background: #F0FDF4; border: 1px solid #BBF7D0; border-radius: var(--radius-md);
                    padding: 12px 14px; display: flex; flex-direction: column; gap: 6px; }
    .support-done .sd-head { display: flex; align-items: center; gap: 8px; font-size: 14px;
                             color: var(--color-slate-900); }
    .support-done .sd-tick { width: 22px; height: 22px; border-radius: 50%; flex: 0 0 auto;
                             display: grid; place-items: center; background: #16A34A; color: #fff;
                             font-size: 13px; font-weight: 700; }
    .support-done p { margin: 0; font-size: 13px; line-height: 1.55; color: var(--color-slate-700); }
    button.ghost { padding: 9px 16px; border-radius: var(--radius-md, 6px); background: var(--color-white);
                   color: var(--color-slate-700); border: 1px solid var(--color-slate-300);
                   cursor: pointer; font-weight: 600; }
    button.ghost:hover { background: var(--color-slate-50); }
    .empty { color: var(--color-slate-500); font-style: italic; }

    /* results / feedback panel */
    .results { display: flex; flex-direction: column; gap: 14px; }
    .results-head { display: flex; align-items: center; gap: 14px; }
    .score-ring { display: flex; flex-direction: column; align-items: center; justify-content: center;
                  width: 86px; height: 86px; border-radius: 50%; flex: 0 0 auto;
                  border: 6px solid var(--color-slate-200); }
    .score-ring.pass { border-color: var(--color-green-500, #16A34A); }
    .score-ring.fail { border-color: var(--color-amber-500, #F59E0B); }
    .score-ring .pct { font-size: 22px; font-weight: 800; color: var(--color-slate-900); line-height: 1; }
    .score-ring .lbl { font-size: 10px; color: var(--color-slate-500); text-transform: uppercase; letter-spacing: .04em; }
    .verdict-text h2 { margin: 0 0 4px; font-size: 18px; }
    .pass-pill { display: inline-block; font-size: 12px; font-weight: 700; padding: 3px 10px; border-radius: 12px;
                 text-transform: uppercase; letter-spacing: .04em; }
    .pass-pill.pass { background: #F0FDF4; color: #16A34A; }
    .pass-pill.fail { background: #FFFBEB; color: #D97706; }
    .verdict-text p { margin: 4px 0 0; font-size: 13px; color: var(--color-slate-500); }
    .feedback-overview { background: var(--color-blue-100); border-radius: var(--radius-md); padding: 12px 14px;
                         font-size: 13px; line-height: 1.55; color: var(--color-slate-700); }
    .result-item { border: 1px solid var(--color-slate-200); border-left-width: 4px; border-radius: var(--radius-md);
                   padding: 10px 12px; }
    .result-item.ok { border-left-color: #16A34A; }
    .result-item.warn { border-left-color: #F59E0B; }
    .result-item.wrong, .result-item.missing { border-left-color: #DC2626; }
    .ri-head { display: flex; align-items: center; gap: 8px; }
    .ri-icon { font-size: 14px; width: 18px; text-align: center; }
    .ri-icon.ok { color: #16A34A; } .ri-icon.warn { color: #F59E0B; }
    .ri-icon.wrong, .ri-icon.missing { color: #DC2626; }
    .ri-field { font-family: var(--font-mono); font-weight: 700; font-size: 13px; }
    .ri-status { font-size: 11px; text-transform: uppercase; letter-spacing: .04em;
                 color: var(--color-slate-500); margin-left: auto; }
    .ri-msg { font-size: 12px; color: var(--color-slate-600); margin-top: 4px; }
    .rationale { font-size: 13px; line-height: 1.55; color: var(--color-slate-700);
                 padding-top: 8px; border-top: 1px solid var(--color-slate-200); white-space: pre-wrap; }

    /* final-attempt confirm dialog */
    .confirm-backdrop { position: fixed; inset: 0; background: rgba(15,23,42,.6);
                         display: grid; place-items: center; z-index: 200; }
    .confirm-modal { background: var(--color-white); padding: 22px; border-radius: 8px;
                      max-width: 400px; box-shadow: 0 12px 32px rgba(0,0,0,.25); }
    .confirm-modal h2 { margin: 0 0 14px; font-size: 16px; color: var(--color-slate-900); }
    .confirm-actions { display: flex; justify-content: flex-end; gap: 10px; }

    /* scenario load-failure screen */
    .load-error { padding: 32px 24px; }
    .load-error h2 { margin: 0 0 8px; font-size: 17px; color: var(--color-slate-900); }
    .load-error p { margin: 0 0 16px; font-size: 13px; line-height: 1.55; color: var(--color-slate-600); }
  `; }
    async connectedCallback() {
        super.connectedCallback();
        await this.loadScenario();
    }
    async loadScenario() {
        this.loadError = null;
        try {
            const raw = await api.getCase(this.scenarioId);
            scenario.value = { ...raw };
            this.assessment = raw.assessment ?? null;
            this.attemptStart = Date.now();
            // Initialise an empty draft BEFORE attempting to restore one, so a draft-restore
            // failure below can't leave draft.value null and null-deref the mode panels.
            if (!draft.value) {
                draft.value = { codes: [], episodeMeta: {}, navigateAnswers: {}, saveStatus: "idle" };
            }
            try {
                const existing = await api.loadDraft(this.scenarioId);
                if (existing) {
                    draft.value = { codes: [], episodeMeta: {}, navigateAnswers: {}, saveStatus: "saved",
                        lastSavedAt: existing.updated_at, ...existing.draft };
                }
            }
            catch (e) {
                console.error("draft restore failed:", e);
            }
            startAutosave(this.scenarioId, (id, d) => api.saveDraft(id, d));
        }
        catch (e) {
            console.error("case-view load failed:", e);
            this.loadError = "Something went wrong loading this activity.";
        }
    }
    retryLoad() {
        this.loadScenario();
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        stopAutosave();
    }
    onDocFocus(e) {
        this.selectedDocId = e.detail.docId;
    }
    async submit() {
        if (this.submitting)
            return; // guard against double-clicks
        this.submitting = true;
        this.submitError = null;
        try {
            const elapsedSec = this.attemptStart
                ? Math.max(0, Math.round((Date.now() - this.attemptStart) / 1000))
                : undefined;
            const payload = { ...draft.value, ...(elapsedSec != null ? { _elapsed_sec: elapsedSec } : {}) };
            const r = await api.submitCase(this.scenarioId, payload);
            if (r.locked) {
                // 4th un-passed attempt refused — show the contact-support screen, but keep this
                // final attempt's (summary-tier) result so it renders above the locked panel.
                this.lockInfo = { attempts_used: r.attempts_used ?? 3, max_attempts: r.max_attempts ?? 3 };
                this.assessment = { attempts_used: r.attempts_used ?? 3, max_attempts: r.max_attempts ?? 3,
                    attempts_remaining: 0, passed: false, locked: true };
                this.result = r;
                return;
            }
            this.result = r;
            if (r.assessment)
                this.assessment = r.assessment;
            this.dispatchEvent(new CustomEvent("submission-result", { detail: r, bubbles: true, composed: true }));
        }
        catch (e) {
            console.error("submit failed:", e);
            this.submitError = "Something went wrong submitting your answers. Check your connection and try again.";
        }
        finally {
            this.submitting = false;
        }
    }
    tryAgain() {
        this.result = null;
        this.submitError = null;
        this.attemptStart = Date.now(); // reset the per-attempt timer
    }
    // The sim runs on ehr.talentmed.com, cross-origin with the LMS page, so it
    // cannot reload the parent directly. It postMessages the LMS, where a small
    // listener (BuddyBoss → Theme Options → Custom Codes → JavaScript) reloads the
    // page after a pass. See reset_controls/lms-bridge.js. (The unlock-support
    // request no longer goes through the LMS — it creates a Zoho Desk ticket via
    // the sim's own backend; see openSupport.)
    static { this.LMS_ORIGIN = "https://my.talentmed.edu.au"; }
    postToLms(payload) {
        const msg = { source: "tm-ehr-sim", ...payload };
        try {
            window.parent?.postMessage(msg, CaseView_1.LMS_ORIGIN);
        }
        catch { /* noop */ }
        try {
            if (window.top && window.top !== window.parent)
                window.top.postMessage(msg, CaseView_1.LMS_ORIGIN);
        }
        catch { /* noop */ }
    }
    /** On a pass, ask the LMS page to reload so Grassblade shows the updated
     *  results table + continue-to-next button (cross-origin: via postMessage). */
    continueToNext() {
        this.postToLms({ action: "reload" });
    }
    scenarioTitle() {
        const sc = scenario.value;
        return (sc && sc.title) || this.scenarioId;
    }
    /** Create a support ticket asking for this assessment to be unlocked. The sim's
     *  backend creates the Zoho Desk ticket (subject + description) with the student
     *  as the contact, so support can reset their attempts with the Desk reset
     *  button. A student can only ever be stuck on one assessment at a time, so the
     *  ticket just needs the activity, the student's name and their email. The
     *  subject and description are composed here (brand voice lives in the sim); the
     *  student's email is taken from the authenticated session on the server. */
    async openSupport() {
        if (this.supportState === "sending" || this.supportState === "sent")
            return;
        const name = (launch.value?.actor?.name ?? "").trim();
        const email = (launch.value?.actor?.mbox ?? "").replace(/^mailto:/, "");
        const activity = this.scenarioTitle();
        const ref = this.scenarioId;
        const who = name || email || "a student";
        const subject = `Assessment unlock request: ${activity}${name ? ` (${name})` : ""}`;
        const description = "Hi Support Team,\n\n" +
            "I'm currently locked out of an assessment activity after using all my attempts, " +
            "and I'd like it reset so I can have another go.\n\n" +
            `Activity: ${activity}\n` +
            `Activity reference: ${ref}\n` +
            `Student: ${who}\n` +
            `Email: ${email}\n\n` +
            "Thanks very much.";
        this.supportState = "sending";
        try {
            const r = await api.requestUnlock({ subject, description, name });
            this.supportTicket = r.ticket_number ?? null;
            this.supportState = "sent";
        }
        catch (e) {
            console.error("unlock request failed:", e);
            this.supportState = "error";
        }
    }
    /** If `label` is a bare code the student has a card for (with a description), show
     *  "CODE Title" instead of the bare code — never derive the label from raw field names. */
    withCodeTitle(label) {
        const match = (draft.value?.codes ?? []).find((c) => c.code === label);
        return match?.description ? `${label} ${match.description}` : label;
    }
    renderResults(r, level) {
        const pct = Math.round(r.score_pct ?? 0);
        const ring = html `<div class="score-ring ${r.passed ? "pass" : "fail"}">
      <div class="pct">${pct}%</div><div class="lbl">score</div></div>`;
        const head = html `<div class="results-head">${ring}
      <div class="verdict-text"><h2>${r.passed ? "Pass" : "Not yet"}</h2>
        <span class="pass-pill ${r.passed ? "pass" : "fail"}">${r.passed ? "Meets pass mark" : "Below pass mark"}</span>
        <p>${level === "score" ? "Detailed feedback is shown by your tutor." : "Review the breakdown below."}</p>
      </div></div>`;
        if (level === "score") {
            return html `<div class="results">${head}</div>`;
        }
        const icon = { ok: "✓", warn: "⚠", wrong: "✗", missing: "○" };
        const items = (r.per_field ?? []).map((f) => {
            // display_label is the human label the backend already computed (contract 2b/4);
            // never parse it back out of the raw field name.
            const label = f.display_label ?? (/^f\d+$/i.test(f.field) ? f.field.toUpperCase() : f.field);
            return html `
      <div class="result-item ${f.status}">
        <div class="ri-head">
          <span class="ri-icon ${f.status}">${icon[f.status] ?? "•"}</span>
          <span class="ri-field">${this.withCodeTitle(label)}</span>
          <span class="ri-status">${f.status}</span>
        </div>
        <div class="ri-msg">${f.message}</div>
      </div>`;
        });
        return html `<div class="results">
      ${head}
      ${items}
      ${(level === "full" || level === "summary") && r.rationale_md
            ? html `<div class="rationale">${unsafeHTML(marked.parse(r.rationale_md, { async: false }))}</div>`
            : ""}
    </div>`;
    }
    renderPassed() {
        return html `
      <div class="results">
        <div class="results-head">
          <div class="score-ring pass"><div class="pct">✓</div><div class="lbl">passed</div></div>
          <div class="verdict-text">
            <h2>Assessment passed</h2>
            <span class="pass-pill pass">Complete</span>
            <p>You've passed this assessment. Continue to the next activity.</p>
          </div>
        </div>
      </div>`;
    }
    renderLocked() {
        const max = this.assessment?.max_attempts ?? this.lockInfo?.max_attempts ?? 3;
        return html `
      <div class="locked">
        <div class="lhead">
          <div class="licon">!</div>
          <h2>You've used all ${max} attempts</h2>
        </div>
        <p>You've reached the maximum number of attempts for this assessment activity and
           it's now locked. Our support team can review your progress and reset it so you
           can have another go.</p>
        <div class="lcard">Activity: <strong>${this.scenarioTitle()}</strong></div>
        ${this.renderSupport()}
      </div>`;
    }
    renderSupport() {
        if (this.supportState === "sent") {
            return html `
        <div class="support-done">
          <div class="sd-head"><span class="sd-tick">✓</span><strong>Request sent</strong></div>
          <p>Our support team has your request to unlock this activity${this.supportTicket
                ? html ` (reference <strong>#${this.supportTicket}</strong>)`
                : ""}. We'll reset it and email you when you can have another go.</p>
        </div>`;
        }
        if (this.supportState === "error") {
            return html `
        <button class="support" @click=${this.openSupport}>Try again</button>
        <p class="support-err">Sorry, we couldn't send your request just then. Please try
           again in a moment, or email <strong>support@talentmed.edu.au</strong> and mention
           this activity.</p>`;
        }
        const sending = this.supportState === "sending";
        return html `
      <button class="support" ?disabled=${sending} @click=${this.openSupport}>
        ${sending ? html `<span class="spinner"></span> Sending request…` : "Contact support to unlock"}
      </button>
      <p>Click the button and we'll send our support team a request to unlock this activity,
         with your details attached. There's nothing else you need to do, we'll review it and
         get you back on track.</p>`;
    }
    renderLoadError() {
        return html `
      <div class="load-error">
        <h2>We couldn't load this activity</h2>
        <p>${this.loadError} Check your connection and try again.</p>
        <button class="submit" @click=${this.retryLoad}>Retry</button>
      </div>`;
    }
    renderFinalConfirm() {
        return html `
      <div class="confirm-backdrop" role="dialog" aria-modal="true" aria-labelledby="final-confirm-title">
        <div class="confirm-modal">
          <h2 id="final-confirm-title">This is your last attempt. Submit now?</h2>
          <div class="confirm-actions">
            <button class="ghost" @click=${() => { this.confirmFinalSubmit = false; }}>Cancel</button>
            <button class="submit" @click=${() => { this.confirmFinalSubmit = false; this.submit(); }}>Confirm</button>
          </div>
        </div>
      </div>`;
    }
    renderPanel(sc) {
        switch (sc.mode) {
            case "classify": return html `<mode-classify .findings=${sc.findings ?? []}></mode-classify>`;
            case "abstract": return html `<mode-abstract .edition=${sc.edition ?? "13e"}
                         .episodePanel=${sc.episode_panel ?? { hmv: true, weight: true, query: true }}></mode-abstract>`;
            case "audit": return html `<mode-audit .presented=${sc.presented_codes ?? []} .edition=${sc.edition ?? "13e"}></mode-audit>`;
            case "sequence": return html `<mode-sequence .unsequenced=${sc.unsequenced_codes ?? []}></mode-sequence>`;
            case "query": return html `<mode-query .routing=${sc.routing}></mode-query>`;
            default: return html `<p class="empty">Mode "${sc.mode}" not supported.</p>`;
        }
    }
    render() {
        const sc = scenario.value;
        if (!sc) {
            return this.loadError ? this.renderLoadError() : html `<p class="empty" style="padding:24px">Loading scenario…</p>`;
        }
        const docs = sc.documents ?? [];
        const currentDoc = (this.selectedDocId
            ? docs.find((d) => d.id === this.selectedDocId)
            : docs[0]) ?? null;
        return html `
      <pas-banner .patient=${sc.patient}></pas-banner>
      <div class="body">
        <div class="pane nav">
          ${docs.length
            ? html `<chart-navigator .documents=${docs} .selectedId=${this.selectedDocId} @doc-focus=${this.onDocFocus}></chart-navigator>`
            : html `<p class="empty" style="padding:16px">No chart documents.</p>`}
        </div>
        <div class="pane chart">
          ${currentDoc
            ? html `<chart-viewer .document=${currentDoc}></chart-viewer>`
            : html `<p class="empty">Select a document.</p>`}
        </div>
        <div class="pane panel">
          ${this.assessment?.locked
            ? html `${this.result
                ? this.renderResults(this.result, this.result.feedback_level ?? "summary")
                : ""}
              ${this.renderLocked()}`
            : this.result
                ? this.renderResults(this.result, this.result.feedback_level
                    ?? sc.feedback_level
                    ?? "full")
                : (sc.routing === "assessment-auto" && this.assessment?.passed)
                    ? this.renderPassed()
                    : this.renderPanel(sc)}
        </div>
      </div>
      ${this.confirmFinalSubmit ? this.renderFinalConfirm() : ""}
      ${this.renderActions(sc)}
    `;
    }
    renderActions(sc) {
        if (this.assessment?.locked) {
            // Locked: the contact-support CTA lives in the panel; no submit controls.
            return html `<div class="actions"><span class="attempt-counter final">No attempts remaining</span></div>`;
        }
        // Already passed (e.g. after a reload): offer continue, not another submit.
        if (!this.result && sc.routing === "assessment-auto" && this.assessment?.passed) {
            return html `<div class="actions">
        <span class="attempt-counter">Assessment passed.</span>
        <button class="submit" @click=${this.continueToNext}>Continue →</button>
      </div>`;
        }
        if (this.result) {
            // After a graded attempt: pass -> continue (refreshes the LMS); fail -> try again.
            if (this.result.passed) {
                return html `<div class="actions">
          <span class="attempt-counter">Result saved.</span>
          <button class="submit" @click=${this.continueToNext}>Continue →</button>
        </div>`;
            }
            const remaining = this.assessment?.attempts_remaining;
            return html `<div class="actions">
        ${remaining != null
                ? html `<span class="attempt-counter ${remaining <= 1 ? "final" : ""}">
                   ${remaining} attempt${remaining === 1 ? "" : "s"} remaining</span>`
                : ""}
        <button class="ghost" @click=${this.tryAgain}>← Edit and try again</button>
      </div>`;
        }
        // Editing: show the attempt counter (assessment only) + submit.
        const a = this.assessment;
        const showCounter = sc.routing === "assessment-auto" && a;
        const current = a ? a.attempts_used + 1 : 0;
        const isFinal = a ? current >= a.max_attempts : false;
        return html `<div class="actions">
      ${showCounter
            ? html `<span class="attempt-counter ${isFinal ? "final" : ""}">
                 Attempt ${current} of ${a.max_attempts}${isFinal ? " (final attempt)" : ""}</span>`
            : html `<save-indicator></save-indicator>`}
      ${this.submitError ? html `<span class="submit-error">${this.submitError}</span>` : ""}
      <button class="submit" ?disabled=${this.submitting}
              @click=${() => { isFinal ? (this.confirmFinalSubmit = true) : this.submit(); }}>
        ${this.submitting
            ? html `<span class="spinner"></span> Evaluating…`
            : "Submit for evaluation"}
      </button>
    </div>`;
    }
};
__decorate([
    property({ type: String })
], CaseView.prototype, "scenarioId", void 0);
__decorate([
    state()
], CaseView.prototype, "selectedDocId", void 0);
__decorate([
    state()
], CaseView.prototype, "result", void 0);
__decorate([
    state()
], CaseView.prototype, "submitting", void 0);
__decorate([
    state()
], CaseView.prototype, "submitError", void 0);
__decorate([
    state()
], CaseView.prototype, "assessment", void 0);
__decorate([
    state()
], CaseView.prototype, "lockInfo", void 0);
__decorate([
    state()
], CaseView.prototype, "supportState", void 0);
__decorate([
    state()
], CaseView.prototype, "supportTicket", void 0);
__decorate([
    state()
], CaseView.prototype, "loadError", void 0);
__decorate([
    state()
], CaseView.prototype, "confirmFinalSubmit", void 0);
CaseView = CaseView_1 = __decorate([
    customElement("case-view")
], CaseView);
export { CaseView };
